import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
// eslint-disable-next-line no-restricted-imports -- useStore needed for store ref passed into step lifecycle callbacks; refactor tracked separately
import { connect, useStore } from "react-redux";

import { uniqBy } from "lodash-es";
import { defineMessages, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import MmcTourTooltip from "@app/component/flows/base/MmcTour/MmcTourTooltip";
import useWaitForUserActions from "@app/component/flows/base/MmcTour/useWaitForUserActions";
import TourDescriptor, { TourStep } from "@app/component/flows/types/TourDescriptor";
import { getRole } from "@app/store/iam";
import type { Actions, RootState } from "@app/store/rootReducer";
import useInterval from "@app/util/hook/useInterval";

const messages = defineMessages({
  closeButton: {
    id: "guides.tour.tooltip.buttons.close",
    defaultMessage: "Close",
    description: "Close button title in a Tour's tooltip",
  },
  doneButton: {
    id: "guides.tour.tooltip.buttons.done",
    defaultMessage: "Done",
    description: "Done button title in a Tour's tooltip",
  },
  errorTitle: {
    id: "guides.tour.tooltip.errorTitle",
    defaultMessage: "Tour Paused",
    description: "Done button title in a Tour's tooltip",
  },
});

const getStepKey = (step: TourStep) => step.key ?? step.id;

export interface MmcTourProps {
  descriptor: TourDescriptor;
  onComplete?: (id: TourDescriptor["id"]) => void;
  onHide?: (id: TourDescriptor["id"]) => void;
  onStepComplete?: (id: TourDescriptor["id"], step: TourStep) => void;
  visible?: boolean;
}

const MmcTour = ({
  currentUserRole,
  descriptor,
  onComplete,
  onHide,
  onStepComplete,
  visible,
}: MmcTourProps & ReturnType<typeof mapStateToProps>) => {
  const store = useStore<RootState, Actions>();

  const intl = useIntl();
  const history = useHistory();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const expectedPush = useRef(false);
  const initializingStep = useRef(false);

  const { eligibleSteps, eligibleStepsCount } = useMemo(() => {
    const eligibleSteps = descriptor.steps.filter(
      ({ eligibleRoles }) => eligibleRoles?.includes(currentUserRole.key) ?? true
    );
    const eligibleStepsCount = uniqBy(
      eligibleSteps.filter((step) => step.description),
      getStepKey
    ).length;
    return { eligibleSteps, eligibleStepsCount };
  }, [descriptor.steps, currentUserRole.key]);

  const effectiveCurrentStepIndex = uniqBy(
    eligibleSteps.filter((step, index) => index <= currentStepIndex && step.description),
    getStepKey
  ).length;

  useEffect(() => {
    if (!eligibleSteps.length) {
      onComplete?.(descriptor.id);
    }
  }, [descriptor.id, eligibleSteps.length, onComplete]);

  useEffect(() => {
    return history.listen((locationState, action) => {
      if (action === "POP") {
        // halting tour if user clicked Back in browser
        onHide?.(descriptor.id);
      }
      if (action === "PUSH") {
        if (expectedPush.current || initializingStep.current) {
          expectedPush.current = false;
        } else {
          // halting tour if user moved to some other page
          onHide?.(descriptor.id);
        }
      }
    });
  }, [history, onHide, descriptor.id]);

  const [error, setError] = useState<ReactNode | undefined>();

  const [goToStep, setGoToStep] = useState<TourStep["id"] | undefined>(undefined);

  const handleStepBefore = useCallback(
    async (step?: TourStep) => {
      initializingStep.current = true;
      if (step?.goToPathBefore) {
        history.push(step.goToPathBefore);
      }
      if (step?.onBefore) {
        const result = await step.onBefore({ stepId: step.id, store, tourId: descriptor.id });
        initializingStep.current = false;
        switch (result?.action) {
          case "error":
            setError(result.message);
            return false;
          case "navigate":
            setGoToStep(result.stepId);
            return false;
        }
      }
      return true;
    },
    [descriptor.id, history, store]
  );

  const handleStepAfter = useCallback(
    async (step?: TourStep) => {
      if (step?.goToPathAfter) {
        expectedPush.current = true;
        history.push(step.goToPathAfter);
      }

      if (step?.onAfter) {
        const result = await step.onAfter({ stepId: step.id, store, tourId: descriptor.id });
        initializingStep.current = false;
        switch (result?.action) {
          case "navigate":
            setGoToStep(result.stepId);
            return false;
        }
      }

      return true;
    },
    [descriptor.id, history, store]
  );

  const [stepVisible, setStepVisible] = useState(false);

  const handleTransition = useDynamicCallback(async (fromIndex: number, toIndex: number) => {
    // console.group("!!! transition");
    // console.log("!!! going from step", fromIndex, eligibleSteps);
    if (fromIndex >= 0) {
      setStepVisible(false);
      const result = await handleStepAfter(eligibleSteps[fromIndex]);
      onStepComplete?.(descriptor.id, eligibleSteps[fromIndex]);
      if (!result) {
        return;
      }
    }
    // console.log("!!! ...to step", toIndex, eligibleSteps);
    if (toIndex < eligibleSteps.length) {
      const result = await handleStepBefore(eligibleSteps[toIndex]);
      if (result) {
        expectedPush.current = eligibleSteps[toIndex].options?.expectRouteChange ?? false;
        setCurrentStepIndex(toIndex);
        setStepVisible(true);
      }
    } else {
      // console.log("!!! onComplete called");
      onComplete?.(descriptor.id);
    }
    // console.log("!!! done");
    // console.groupEnd();
  });

  useEffect(() => {
    if (!visible) {
      return;
    }
    handleTransition(-1, 0);
  }, [handleTransition, visible]);

  const handleCloseClick = useCallback(() => {
    onHide?.(descriptor.id);
  }, [descriptor.id, onHide]);

  const [nextButtonDisabled, setNextButtonDisabled] = useState(false);

  const handleNextClick = useDynamicCallback(() => {
    setCurrentStepIndex((index) => {
      handleTransition(index, index + 1);
      // we don't change index yet
      return index;
    });
  });

  useEffect(() => {
    if (goToStep === undefined) {
      return;
    }

    const targetStepIndex = eligibleSteps.findIndex(({ id }) => id === goToStep);
    setGoToStep(undefined);

    if (targetStepIndex < 0) {
      return;
    }

    setCurrentStepIndex((index) => {
      onStepComplete?.(descriptor.id, eligibleSteps[index]);
      // we use -1, because we don't need to run onAfter for the previous step again
      handleTransition(-1, targetStepIndex);
      // not changing the current step index yet, handleTransition will do that for us
      return index;
    });
  }, [goToStep, eligibleSteps, handleTransition, onStepComplete, descriptor.id]);

  const step = eligibleSteps[currentStepIndex];

  const handleElementFound = useDynamicCallback(async () => {
    if (step.onShow) {
      const result = await step.onShow({ stepId: step.id, store, tourId: descriptor.id });
      switch (result?.action) {
        case "error":
          setError(result.message);
          break;
        case "navigate":
          setGoToStep(result.stepId);
          break;
      }
    }
  });

  useInterval(
    useDynamicCallback(() => {
      setNextButtonDisabled(!step.canGoNext!({ stepId: step.id, store, tourId: descriptor.id }));
    }),
    !!(visible && step?.canGoNext),
    250
  );

  useWaitForUserActions(step, handleNextClick);

  if (!visible || !step) {
    return null;
  }

  return error ? (
    <MmcTourTooltip
      content={error}
      nextButtonText={intl.formatMessage(messages.closeButton)}
      onHide={handleCloseClick}
      onNextClick={handleCloseClick}
      title={intl.formatMessage(messages.errorTitle)}
      visible
    />
  ) : stepVisible && step.description ? (
    <MmcTourTooltip
      content={intl.formatMessage(step.description, {
        b: (texts: ReactNode[]) => <b>{texts}</b>,
        br: <br />,
        i: (texts: ReactNode[]) => <i>{texts}</i>,
        // eslint-disable-next-line id-length -- FormatJS rich-text tag name must match the <p> tag in the localized message
        p: (texts: ReactNode[]) => <p>{texts}</p>,
      })}
      nextButtonDisabled={nextButtonDisabled}
      nextButtonText={
        currentStepIndex === eligibleSteps.length - 1
          ? intl.formatMessage(messages.doneButton)
          : undefined
      }
      onElementFound={handleElementFound}
      onHide={handleCloseClick}
      onNextClick={handleNextClick}
      position={step.position}
      progress={`${effectiveCurrentStepIndex} / ${eligibleStepsCount}`}
      selector={step.selector}
      selectorFoundDelay={step.selectorFoundDelay}
      title={step.title ? intl.formatMessage(step.title) : undefined}
      {...step.options}
      visible
    />
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  currentUserRole: getRole(state)!,
});

export default connect(mapStateToProps, {})(MmcTour);
